/* eslint-disable no-await-in-loop */
/* eslint-disable no-continue */
/* eslint-disable no-restricted-syntax */

import { unique } from '@sveltia/utils/array';
import { getPathInfo, readAsText } from '@sveltia/utils/file';
import { escapeRegExp, stripSlashes } from '@sveltia/utils/string';
import { get } from 'svelte/store';

import { allAssets } from '$lib/services/assets';
import { allAssetFolders } from '$lib/services/assets/folders';
import { getAssetKind } from '$lib/services/assets/kinds';
import { GIT_CONFIG_FILE_REGEX, gitConfigFiles } from '$lib/services/backends/git/shared/config';
import { createFileList } from '$lib/services/backends/process';
import { allEntries, allEntryFolders, dataLoaded, entryParseErrors } from '$lib/services/contents';
import { ESCAPED_PLACEHOLDER_REGEX } from '$lib/services/contents/file/config';
import { prepareEntries } from '$lib/services/contents/file/process';
import { isBrave } from '$lib/services/user/env';
import { createPathRegEx, getBlob, getGitHash } from '$lib/services/utils/file';

/**
 * @import {
 * Asset,
 * BaseAssetListItem,
 * BaseConfigListItem,
 * BaseEntryListItem,
 * BaseFileListItem,
 * BaseFileListItemProps,
 * CommitResults,
 * FileChange,
 * } from '$lib/types/private';
 */

/**
 * File handle item containing metadata and handle reference.
 * @typedef {object} FileHandleItem
 * @property {FileSystemFileHandle} handle File system handle.
 * @property {string} path Path to the file.
 */

/**
 * Maximum file size in bytes to read content from. (10 MB).
 */
const MAX_FILE_SIZE = 10 * 1024 * 1024;
/**
 * Batch size for processing files to balance performance with memory safety.
 * @see https://github.com/sveltia/sveltia-cms/issues/224
 */
const FILE_PROCESS_BATCH_SIZE = 10;

/**
 * Get a file or directory handle at the given path.
 * @internal
 * @param {FileSystemDirectoryHandle} rootDirHandle Root directory handle.
 * @param {string | undefined} path Path to the file/directory.
 * @param {'file' | 'directory'} [type] Type of the handle to retrieve.
 * @returns {Promise<FileSystemFileHandle | FileSystemDirectoryHandle>} Handle.
 * @throws {Error} If the path is empty and the type is `file`.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/FileSystemDirectoryHandle/getFileHandle
 * @see https://developer.mozilla.org/en-US/docs/Web/API/FileSystemDirectoryHandle/getDirectoryHandle
 */
export const getHandleByPath = async (rootDirHandle, path, type = 'file') => {
  const normalizedPath = stripSlashes(path ?? '');
  /** @type {FileSystemFileHandle | FileSystemDirectoryHandle} */
  let handle = rootDirHandle;

  if (!normalizedPath) {
    if (type === 'directory') {
      return handle;
    }

    throw new Error('Path is required for file handle retrieval');
  }

  const pathParts = normalizedPath.split('/');
  const lastIndex = pathParts.length - 1;
  const create = true;

  for await (const [index, name] of pathParts.entries()) {
    // If the part is the last one and the type is `file`, we need to ensure that we get a file
    // handle. Otherwise, we can get a directory handle.
    handle = await (index === lastIndex && type === 'file'
      ? /** @type {FileSystemDirectoryHandle} */ (handle).getFileHandle(name, { create })
      : /** @type {FileSystemDirectoryHandle} */ (handle).getDirectoryHandle(name, { create }));
  }

  return handle;
};

/**
 * Get a file handle at the given path. This function is used to retrieve a file handle for reading
 * or writing a file. If the file does not exist, it will be created.
 * @param {FileSystemDirectoryHandle} rootDirHandle Root directory handle.
 * @param {string} path Path to the file.
 * @returns {Promise<FileSystemFileHandle>} Handle.
 * @throws {Error} If the path is empty.
 */
export const getFileHandle = (rootDirHandle, path) =>
  /** @type {Promise<FileSystemFileHandle>} */ (getHandleByPath(rootDirHandle, path, 'file'));

/**
 * Get a directory handle at the given path. This function is used to retrieve a directory handle
 * for reading or writing files within a directory. If the directory does not exist, it will be
 * created.
 * @param {FileSystemDirectoryHandle} rootDirHandle Root directory handle.
 * @param {string | undefined} path Path to the directory.
 * @returns {Promise<FileSystemDirectoryHandle>} Handle.
 */
export const getDirectoryHandle = (rootDirHandle, path) =>
  /** @type {Promise<FileSystemDirectoryHandle>} */ (
    getHandleByPath(rootDirHandle, path, 'directory')
  );

/**
 * Create a regular expression that matches the given path, taking template tags into account.
 * @internal
 * @param {string} path Path.
 * @returns {RegExp} RegEx.
 */
export const getPathRegex = (path) => {
  // Handle empty path (root folder) - match any file
  if (!path) {
    return /^.+$/;
  }

  return createPathRegEx(path, (segment) =>
    escapeRegExp(segment).replace(ESCAPED_PLACEHOLDER_REGEX, '.+?'),
  );
};

/**
 * Retrieve all the files under the given directory recursively.
 * @internal
 * @param {FileSystemDirectoryHandle} dirHandle Directory handle.
 * @param {object} context Context object.
 * @param {FileSystemDirectoryHandle} context.rootDirHandle Root directory handle.
 * @param {string[]} context.scanningPaths Scanning paths.
 * @param {RegExp[]} context.scanningPathsRegEx Regular expressions for scanning paths.
 * @param {FileHandleItem[]} context.fileHandles List of available file handles.
 * @param {Map<string, RegExp>} context.pathRegexCache Cache for path regexes.
 * @param {string} [currentPath] Current directory path (for recursion).
 */
export const scanDir = async (dirHandle, context, currentPath = '') => {
  const { scanningPaths, scanningPathsRegEx, fileHandles, pathRegexCache } = context;

  for await (const [name, handle] of dirHandle.entries()) {
    // Skip hidden files and directories, except for Git configuration files
    if (name.startsWith('.') && !GIT_CONFIG_FILE_REGEX.test(name)) {
      continue;
    }

    const path = currentPath ? `${currentPath}/${name}` : name;
    const hasMatchingPath = scanningPathsRegEx.some((regex) => regex.test(path));

    if (handle.kind === 'file' && hasMatchingPath) {
      // Store only the handle and path. Metadata will be extracted later when needed, avoiding
      // memory leaks from holding multiple file references during directory scanning.
      fileHandles.push({
        handle: /** @type {FileSystemFileHandle} */ (handle),
        path,
      });
    }

    if (handle.kind === 'directory') {
      // Cache regex creation to avoid recreating for the same path
      let regex = pathRegexCache.get(path);

      if (!regex) {
        regex = getPathRegex(path);
        pathRegexCache.set(path, regex);
      }

      if (hasMatchingPath || scanningPaths.some((p) => regex.test(p))) {
        await scanDir(/** @type {FileSystemDirectoryHandle} */ (handle), context, path);
      }
    }
  }
};

/**
 * Collect all scanning paths from entry and asset folders.
 * @internal
 * @returns {string[]} Unique list of normalized scanning paths.
 */
export const collectScanningPaths = () => {
  const entryPaths = get(allEntryFolders).flatMap(({ filePathMap, folderPathMap }) =>
    filePathMap ? Object.values(filePathMap) : Object.values(folderPathMap ?? {}),
  );

  const assetPaths = get(allAssetFolders)
    .filter(({ internalPath }) => internalPath !== undefined)
    .map(({ internalPath }) => internalPath);

  return unique(
    /* v8 ignore next */
    [...entryPaths, ...assetPaths].map((path) => stripSlashes(path ?? '')),
  );
};

/**
 * Retrieve all files under the static directory.
 * @internal
 * @param {FileSystemDirectoryHandle} rootDirHandle Root directory handle.
 * @returns {Promise<BaseFileListItemProps[]>} File list.
 */
export const getAllFiles = async (rootDirHandle) => {
  /** @type {FileHandleItem[]} */
  const fileHandles = [];
  const scanningPaths = collectScanningPaths();

  await scanDir(rootDirHandle, {
    rootDirHandle,
    scanningPaths,
    scanningPathsRegEx: scanningPaths.map(getPathRegex),
    fileHandles,
    pathRegexCache: new Map(),
  });

  return fileHandles.map(({ handle, path }) => ({
    handle,
    path: path.normalize(),
    name: handle.name.normalize(),
    size: 0, // Will be populated later
    sha: '', // Will be populated later
  }));
};

/**
 * Parse text file info to create a complete entry or config file object.
 * @internal
 * @param {BaseFileListItem} fileInfo Entry or config file info.
 * @returns {Promise<BaseFileListItem>} Entry or config file with text content. We don’t populate
 * `size` and `sha` for entries and config files, as they are not needed.
 */
export const parseTextFileInfo = async (fileInfo) => {
  const { name, handle } = fileInfo;

  // Skip `.gitkeep` file, as we don’t need to read its content
  if (name === '.gitkeep') {
    return fileInfo;
  }

  try {
    const file = await /** @type {FileSystemFileHandle} */ (handle).getFile();

    if (file.size > MAX_FILE_SIZE) {
      // eslint-disable-next-line no-console
      console.warn(`File ${name} is too large (${file.size} bytes), skipping content read`);

      return { ...fileInfo, text: '' };
    }

    const text = await readAsText(file);

    return { ...fileInfo, text };
  } catch (ex) {
    // eslint-disable-next-line no-console
    console.error(ex);

    return { ...fileInfo, text: '' };
  }
};

/**
 * Parse asset file info to create a complete asset object.
 * @internal
 * @param {BaseAssetListItem} fileInfo Asset file info.
 * @returns {Promise<Asset>} Asset object.
 */
export const parseAssetFileInfo = async (fileInfo) => {
  const { name, handle } = fileInfo;
  const kind = getAssetKind(name);

  try {
    const file = await /** @type {FileSystemFileHandle} */ (handle).getFile();
    const { size } = file;
    const sha = await getGitHash(file);

    return { ...fileInfo, kind, size, sha };
  } catch (ex) {
    // eslint-disable-next-line no-console
    console.error(ex);

    return { ...fileInfo, kind };
  }
};

/**
 * Load file list and all the entry files from the file system, then cache them in the stores.
 * @param {FileSystemDirectoryHandle} rootDirHandle Root directory handle.
 */
export const loadFiles = async (rootDirHandle) => {
  const { entryFiles, assetFiles, configFiles } = createFileList(await getAllFiles(rootDirHandle));
  /** @type {BaseEntryListItem[]} */
  const entryFileItems = [];
  /** @type {BaseConfigListItem[]} */
  const configFileItems = [];

  // Process files in batches to balance performance with memory safety
  for (let i = 0; i < entryFiles.length; i += FILE_PROCESS_BATCH_SIZE) {
    const batch = entryFiles.slice(i, i + FILE_PROCESS_BATCH_SIZE);
    const results = await Promise.all(batch.map((fileInfo) => parseTextFileInfo(fileInfo)));

    entryFileItems.push(.../** @type {BaseEntryListItem[]} */ (results));
  }

  for (let i = 0; i < configFiles.length; i += FILE_PROCESS_BATCH_SIZE) {
    const batch = configFiles.slice(i, i + FILE_PROCESS_BATCH_SIZE);
    const results = await Promise.all(batch.map((fileInfo) => parseTextFileInfo(fileInfo)));

    configFileItems.push(.../** @type {BaseConfigListItem[]} */ (results));
  }

  const { entries, errors } = await prepareEntries(entryFileItems);
  /** @type {Asset[]} */
  const assets = [];

  for (let i = 0; i < assetFiles.length; i += FILE_PROCESS_BATCH_SIZE) {
    const batch = assetFiles.slice(i, i + FILE_PROCESS_BATCH_SIZE);
    const results = await Promise.all(batch.map((fileInfo) => parseAssetFileInfo(fileInfo)));

    assets.push(...results);
  }

  allEntries.set(entries);
  allAssets.set(assets);
  gitConfigFiles.set(configFileItems);
  entryParseErrors.set(errors);
  dataLoaded.set(true);
};

/**
 * Check if the `move` method is supported by the current browser. The `move` method is not
 * implemented in older browsers, and Brave supports the `move` method but throws an error for some
 * reason, so we need to check it by actually trying to use it.
 * @internal
 * @returns {boolean} `true` if the `move` method is supported, `false` otherwise.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/File_System_API#browser_compatibility
 * @see https://github.com/sveltia/sveltia-cms/discussions/676
 */
export const canMoveFile = () => 'move' in FileSystemFileHandle.prototype && !get(isBrave);

/**
 * Write data to a file using the provided file handle. This function is used to write data to a
 * file when we already have a file handle reference, such as when moving a file without changes. It
 * handles the case where the `createWritable` method is not supported by older versions of Safari.
 * @internal
 * @param {FileSystemFileHandle} fileHandle File handle to write to.
 * @param {FileSystemWriteChunkType} data Data to write to the file.
 */
export const writeFile = async (fileHandle, data) => {
  // The `createWritable` method is not supported by older versions of Safari
  const writer = await fileHandle.createWritable?.();

  try {
    // Can throw if the file has just been moved/renamed without any change, and then the `data` is
    // no longer available
    await writer?.write(data);
  } finally {
    try {
      await writer?.close();
    } catch {
      //
    }
  }
};

/**
 * Move a file from a previous path to a new path within the file system.
 * @internal
 * @param {object} args Arguments.
 * @param {FileSystemDirectoryHandle} args.rootDirHandle Root directory handle.
 * @param {string} args.previousPath The current path of the file to move.
 * @param {string} args.path The new path where the file should be moved.
 * @returns {Promise<FileSystemFileHandle>} Moved file handle.
 */
export const moveFile = async ({ rootDirHandle, previousPath, path }) => {
  const { dirname: newDirname, basename: newBasename } = getPathInfo(path);
  const { dirname: oldDirname, basename: oldBasename } = getPathInfo(previousPath);
  const fileHandle = await getFileHandle(rootDirHandle, previousPath);

  // Use the native `move` method if supported, as it’s more efficient and preserves file metadata.
  // If not, fall back to copying the file to the new location and deleting the old file.
  if (canMoveFile()) {
    // @ts-ignore
    await fileHandle.move(await getDirectoryHandle(rootDirHandle, newDirname), newBasename);

    return fileHandle;
  }

  const newFileHandle = await getFileHandle(rootDirHandle, path);
  const oldDirHandle = await getDirectoryHandle(rootDirHandle, oldDirname);

  await writeFile(newFileHandle, await fileHandle.getFile());
  await oldDirHandle.removeEntry(oldBasename);

  return newFileHandle;
};

/**
 * Save data to a file at the specified path.
 * @internal
 * @param {object} args Arguments.
 * @param {FileSystemDirectoryHandle} args.rootDirHandle Root directory handle.
 * @param {FileSystemFileHandle} [args.fileHandle] File handle to write to. Provided if the file has
 * been moved.
 * @param {string} args.path The relative path to the file within the root directory.
 * @param {string | File} args.data The data to write to the file.
 * @returns {Promise<File>} Written file.
 */
export const saveFile = async ({ rootDirHandle, fileHandle, path, data }) => {
  // When no handle is provided (create/update), write to a temp file first, then rename it to the
  // final path. This avoids race conditions with file watchers (e.g., Astro dev server) that may
  // read the new file before its content is fully written.
  // @see https://github.com/sveltia/sveltia-cms/issues/675
  /** @type {{ dirname: string | undefined, basename: string } | undefined} */
  let pendingRename;

  if (!fileHandle) {
    // Check if the `move` method is supported before deciding whether to write to a temp file, as
    // writing to a temp file and then renaming it is only necessary if the `move` method is not
    // supported. If the `move` method is supported, we have to write directly to the final path.
    if (canMoveFile()) {
      const { dirname, basename } = getPathInfo(stripSlashes(path));
      const tempPath = `${dirname ? `${dirname}/` : ''}.sveltia-tmp-${crypto.randomUUID()}`;

      fileHandle = await getFileHandle(rootDirHandle, tempPath);
      pendingRename = { dirname, basename };
    } else {
      fileHandle = await getFileHandle(rootDirHandle, path);
    }
  }

  await writeFile(fileHandle, data);

  if (pendingRename) {
    const { dirname, basename } = pendingRename;

    // @ts-ignore
    await fileHandle.move(await getDirectoryHandle(rootDirHandle, dirname), basename);
  }

  return fileHandle.getFile();
};

/**
 * Recursively delete empty parent directories.
 * @internal
 * @param {FileSystemDirectoryHandle} rootDirHandle Root directory handle.
 * @param {string[]} pathSegments Array of directory path segments.
 */
export const deleteEmptyParentDirs = async (rootDirHandle, pathSegments) => {
  // Start from the deepest directory
  for (let i = pathSegments.length; i > 0; i -= 1) {
    const dirName = pathSegments[i - 1];
    const parentPath = pathSegments.slice(0, i - 1).join('/');
    const parentHandle = await getDirectoryHandle(rootDirHandle, parentPath);
    const dirHandle = await parentHandle.getDirectoryHandle(dirName);

    // Use for...of to check if directory is empty with early exit on first entry found
    // eslint-disable-next-line no-unreachable-loop
    for await (const _entry of dirHandle.entries()) {
      // Directory is not empty, stop cleanup
      return;
    }

    // Directory is empty, remove it
    await parentHandle.removeEntry(dirName);
  }
};

/**
 * Delete a file at the specified path within the file system.
 * @internal
 * @param {object} args Arguments.
 * @param {FileSystemDirectoryHandle} args.rootDirHandle Root directory handle.
 * @param {string} args.path The path to the file to be deleted.
 */
export const deleteFile = async ({ rootDirHandle, path }) => {
  const { dirname: dirPath = '', basename: fileName } = getPathInfo(stripSlashes(path));
  const dirHandle = await getDirectoryHandle(rootDirHandle, dirPath);

  await dirHandle.removeEntry(fileName);

  if (dirPath) {
    await deleteEmptyParentDirs(rootDirHandle, dirPath.split('/'));
  }
};

/**
 * Save a file to the file system based on the provided change options.
 * @internal
 * @param {FileSystemDirectoryHandle} rootDirHandle Root directory handle.
 * @param {FileChange} change File change options.
 * @returns {Promise<?File>} Created or updated file, if available.
 * @throws {Error} If an error occurs while saving the file.
 */
export const saveChange = async (rootDirHandle, { action, path, previousPath, data }) => {
  /** @type {FileSystemFileHandle | undefined} */
  let fileHandle;

  if (action === 'move' && previousPath) {
    fileHandle = await moveFile({ rootDirHandle, previousPath, path });
  }

  if (['create', 'update', 'move'].includes(action) && data) {
    // We don’t need to write the file is it’s just been renamed with no change, but the `data` is
    // always provided for the compatibility with Git backends, so we cannot distinguish between the
    // two cases
    return saveFile({ rootDirHandle, fileHandle, path, data });
  }

  if (action === 'delete') {
    await deleteFile({ rootDirHandle, path });
  }

  return null;
};

/**
 * Save entries or assets in the file system.
 * @param {FileSystemDirectoryHandle | undefined} rootDirHandle Root directory handle. This can be
 * `undefined` if the directory handle could not be acquired earlier for security reasons. If the
 * handle is not available, the changes will not be saved, but the user can still continue using the
 * app without an error thanks to the in-memory cache.
 * @param {FileChange[]} changes File changes to be saved.
 * @returns {Promise<CommitResults>} Commit results, including a pseudo commit SHA, saved files, and
 * their blob SHAs.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/FileSystemWritableFileStream/write
 * @see https://developer.mozilla.org/en-US/docs/Web/API/FileSystemDirectoryHandle/removeEntry
 */
export const saveChanges = async (rootDirHandle, changes) => {
  const entries = await Promise.all(
    changes.map(async (change) => {
      const { path, data } = change;
      /** @type {Blob | null} */
      let file = null;

      if (rootDirHandle) {
        try {
          file = await saveChange(rootDirHandle, change);
        } catch (ex) {
          // eslint-disable-next-line no-console
          console.error(ex);
        }
      }

      if (!file) {
        if (data === undefined) {
          return null;
        }

        file = getBlob(data);
      }

      return [path, { file, sha: await getGitHash(file) }];
    }),
  );

  return {
    // Use a hash of the current date as a pseudo SHA
    sha: await getGitHash(new Date().toJSON()),
    files: Object.fromEntries(entries.filter((entry) => !!entry)),
  };
};
